import { makeAutoObservable, reaction } from "mobx";
import PubSub from "pubsub-js";

import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";
import { FileExtended } from "e.sic-platform.webclient/src/services/upload/FileUploadTracker";
import {
  shopHistoryStorage,
  RESTORED_PREFIX,
  ShopActivity,
} from "e.sic-platform.webclient/src/services/shop/ShopHistoryStorage";
import { getAppActivity } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import { PubSubEvents } from "e.sic-platform.webclient/src/services/PubSubEvents";
import { restoreDataApp } from "../apps/SmartApps";

export type RestoreOpStatus =
  | "queued"
  | "uploading"
  | "restoring"
  | "completed"
  | "failed"
  | "cancelled";

export interface RestoreOp {
  id: string;
  fileName: string;
  file: FileExtended | null;
  status: RestoreOpStatus;
  fileId?: number;
  restoredGuid?: string;
  restoredActivityType?: number;
  errorMessage?: string;
  // Set when the op was recovered from shopHistoryStorage after a page refresh.
  // For these ops we have no upload blob, can't retry, and can't cancel locally
  // (the backend is processing the restore independently of this client).
  hydrated?: boolean;
}

function uid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Tracks the history + queue of restore operations. The underlying restore
 * pipeline (uploadFilesService + restoreDataService) handles ONE restore at a
 * time, so submissions queue up sequentially. The panel reads from this
 * manager's `operations` array to render the list.
 */
class RestoreManagerService {
  operations: RestoreOp[] = [];

  constructor() {
    makeAutoObservable(this);

    // Mirror the restore service's state onto the currently-active operation
    // so the panel sees status transitions.
    reaction(
      () => {
        const svc: any = restoreDataApp?.services?.restoreDataService;
        return {
          fileId: restoreDataApp?.fileId,
          isConverted: svc?.isConverted,
          restoreFailed: svc?.restoreFailed,
          restoreRunning: svc?.restoreRunning,
          restoredGuid: svc?.restoredGuid,
          restoredActivityType: svc?.restoredActivityType,
          message: svc?.message,
          uploadVisible: (uploadFilesService as any)?.uploadProgressModalIsVisbile,
        };
      },
      (s) => {
        const active = this.activeOp();
        if (!active) return;
        if (s.isConverted) {
          active.status = "completed";
          active.fileId = s.fileId;
          active.restoredGuid = s.restoredGuid;
          active.restoredActivityType = s.restoredActivityType;
          this.processNext();
        } else if (s.restoreFailed) {
          active.status = "failed";
          active.errorMessage = s.message || "";
          this.processNext();
        } else if (s.restoreRunning) {
          active.status = "restoring";
          active.fileId = s.fileId;
        } else if (s.uploadVisible && s.fileId === undefined) {
          active.status = "uploading";
        }
      }
    );

    // Recovery after a page refresh: the manager is in-memory only, so on a
    // fresh page load `operations` is empty even if the backend is still
    // processing a restore. Watch the global shop-history (which is filtered
    // to Running items) and surface any [Restored] files we don't already
    // track. Also reconcile hydrated ops whose fileId is no longer Running.
    reaction(
      () => shopHistoryStorage.history,
      () => this.hydrateFromHistory(),
      { fireImmediately: true }
    );

    // When the server reports a restore finished, mark any matching hydrated
    // op as completed so the panel stops showing it as in-progress. The
    // notification's data fileId identifies the restored output file.
    PubSub.subscribe(
      PubSubEvents.RestoreDataFinished,
      (_event: string, notification: any) => {
        const fileId = Number(notification?.data?.fileId);
        if (!Number.isFinite(fileId) || fileId <= 0) return;
        const op = this.operations.find(
          (o) => o.hydrated && o.fileId === fileId && o.status === "restoring"
        );
        if (op) op.status = "completed";
      }
    );

    // When the server reports a restore has STARTED (fired right after the
    // backend flips OcrIsInProgress=true on the zip), reload shop history so
    // the left-menu Inspection-Insights counter increments immediately. Without
    // this, the running flag would only be visible AFTER the restore PUT
    // returns — at which point the file has already finished and dropped out
    // of the Running filter, so the counter would never tick up at all.
    PubSub.subscribe(
      PubSubEvents.RestoreDataStarted,
      () => {
        void shopHistoryStorage.load();
      }
    );
  }

  private hydrateFromHistory() {
    // Skip if a brand-new local submission is mid-flight without a fileId yet —
    // we'd risk creating a duplicate hydrated op for it. The next history
    // reload (after fileId is assigned) will see it and dedup correctly.
    const inFlightWithoutId = this.operations.some(
      (o) =>
        (o.status === "uploading" || o.status === "queued") &&
        o.fileId === undefined &&
        !o.hydrated
    );
    if (inFlightWithoutId) return;

    const trackedIds = new Set(
      this.operations
        .filter((o) => o.fileId !== undefined)
        .map((o) => o.fileId as number)
    );
    const runningIds = new Set<number>();

    shopHistoryStorage.history.forEach((item: ShopActivity) => {
      if (!item || !item.fileId || item.fileId <= 0) return;
      const name = item.filename ?? "";
      if (!name.startsWith(RESTORED_PREFIX)) return;
      // A genuine restore-in-progress is a backup .zip still being processed. A
      // restored file that is Running because the user re-ran a (paid) OCR/analysis
      // — i.e. a Redo — is already a converted video. Skip those so a Redo never
      // surfaces the restore panel. (contentType is absent on older data -> keep
      // the previous behaviour and treat it as a possible restore.)
      const ct = (item.contentType ?? "").toLowerCase();
      const isConvertedVideo = ct.startsWith("video/") || ct.includes("mp4");
      if (isConvertedVideo) return;
      runningIds.add(item.fileId);
      if (trackedIds.has(item.fileId)) return;
      this.operations.push({
        id: "hydrated-" + item.fileId,
        fileName: name.slice(RESTORED_PREFIX.length),
        file: null,
        status: "restoring",
        fileId: item.fileId,
        restoredGuid: item.guid,
        restoredActivityType: getAppActivity(item.services),
        hydrated: true,
      });
    });

    // A hydrated op whose fileId is no longer Running has finished on the
    // server (Success, Failed, or Deleted). Without inspecting the file we
    // can't tell which — surface as completed; user can open the link to see.
    this.operations.forEach((op) => {
      if (!op.hydrated || op.status !== "restoring") return;
      if (op.fileId && !runningIds.has(op.fileId)) {
        op.status = "completed";
      }
    });
  }

  private activeOp(): RestoreOp | undefined {
    return this.operations.find(
      (o) => o.status === "queued" || o.status === "uploading" || o.status === "restoring"
    );
  }

  get hasActive(): boolean {
    return !!this.activeOp();
  }

  submit(file: FileExtended) {
    const op: RestoreOp = {
      id: uid(),
      fileName: file.file.name,
      file,
      status: "queued",
    };
    this.operations.push(op);
    this.processNext();
  }

  private async processNext() {
    // If something is already uploading or restoring, wait.
    const active = this.operations.find(
      (o) => o.status === "uploading" || o.status === "restoring"
    );
    if (active) return;

    const next = this.operations.find((o) => o.status === "queued");
    if (!next || !next.file) return;

    next.status = "uploading";
    // Clear any previous restore state and the upload service before starting.
    if (typeof restoreDataApp.new === "function") {
      await restoreDataApp.new();
    }
    uploadFilesService.clear();
    uploadFilesService.addFilesForUpload([next.file]);
    restoreDataApp.upload();
  }

  cancel(id: string) {
    const op = this.operations.find((o) => o.id === id);
    if (!op) return;
    // Hydrated ops have no local upload/blob — cancelling locally won't reach
    // the backend's in-flight restore. Just drop the entry from the panel; the
    // server-side operation will continue and finalize via SignalR.
    if (op.hydrated) {
      const idx = this.operations.findIndex((o) => o.id === id);
      if (idx !== -1) this.operations.splice(idx, 1);
      return;
    }
    if (op.status === "uploading" || op.status === "restoring") {
      const svc: any = restoreDataApp?.services?.restoreDataService;
      svc?.cancelRestore?.();
      // Force the active op to cancelled so the queue can move on.
    }
    op.status = "cancelled";
    if (typeof restoreDataApp.new === "function") {
      restoreDataApp.new();
    }
    // Backend may not always send RestoreDataFinished for an aborted op, so
    // refresh shop history locally to immediately update the left-menu counter.
    void shopHistoryStorage.load();
    this.processNext();
  }

  retry(id: string) {
    const op = this.operations.find((o) => o.id === id);
    if (!op || !op.file) return;
    op.status = "queued";
    op.errorMessage = undefined;
    this.processNext();
  }

  dismiss(id: string) {
    const idx = this.operations.findIndex((o) => o.id === id);
    if (idx === -1) return;
    const op = this.operations[idx];
    // Only dismiss completed / failed / cancelled.
    if (op.status === "completed" || op.status === "failed" || op.status === "cancelled") {
      this.operations.splice(idx, 1);
    }
  }

  clearFinished() {
    this.operations = this.operations.filter(
      (o) => o.status === "queued" || o.status === "uploading" || o.status === "restoring"
    );
  }
}

export const restoreManager = new RestoreManagerService();
